import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { DetectionEntity } from '../types';
import { EntityWithFieldMetadata } from '../../UI/dynamicUI/types';

type DeleteEntityVariables = {
  clientId: string;
  entityType: string;
  entityId: string;
};

export function useDeleteEntities() {
  const queryClient = useQueryClient();
  const { flagEnabledBatchCrudDataUpload } = useFlags();

  const {
    mutateAsync,
    isError,
    isPending: isLoading,
    reset,
  } = useMutation<unknown, Error, DeleteEntityVariables>({
    mutationFn: ({ clientId, entityType, entityId }) =>
      api.delete(`clients/${clientId}/entity-types/${entityType}/entities/${entityId}`),
    onSuccess: (_data, { clientId, entityType, entityId }) => {
      queryClient.removeQueries({
        queryKey: ['clients', clientId, entityType, 'entities', entityId],
      });
      queryClient.invalidateQueries({
        queryKey: ['clients', clientId],
        predicate: flagEnabledBatchCrudDataUpload
          ? query => !query.queryKey.includes('detections')
          : undefined,
      });
    },
  });

  const deleteEntities = async (entities: DetectionEntity[], clientId: string) => {
    const deleteRequests = entities.map(entity => {
      const entityId = entity.fields.id?.toString();
      if (!entityId) {
        return;
      }
      return mutateAsync({ clientId, entityType: entity.entityType, entityId });
    });
    await Promise.all(deleteRequests);
  };

  const deleteEntityByTypeAndId = async (
    clientId: string,
    entityType: string,
    entityId: string,
  ) => {
    if (!entityId) {
      return;
    }
    return mutateAsync({ clientId, entityType, entityId });
  };

  const deleteEntitiesBatch = async (
    clientId: string,
    entities: { entityType: string; entityId: string }[],
  ) => {
    if (entities.length === 0) return;
    await api.delete(
      `clients/${clientId}/entities`,
      entities.map(e => ({ entityType: e.entityType, entityId: e.entityId })),
    );
    entities.forEach(({ entityType, entityId }) => {
      queryClient.removeQueries({
        queryKey: ['clients', clientId, entityType, 'entities', entityId],
      });
    });
    queryClient.invalidateQueries({
      queryKey: ['clients', clientId],
      predicate: query => !query.queryKey.includes('detections'),
    });
  };

  const deleteEntity = async (entity: EntityWithFieldMetadata, clientId: string) => {
    const entityId = entity.fields.id?.toString();
    if (!entityId) {
      return;
    }
    return deleteEntityByTypeAndId(clientId, entity.entityType, entityId);
  };

  return {
    deleteEntities,
    deleteEntity,
    deleteEntityByTypeAndId,
    deleteEntitiesBatch,
    isError,
    isLoading,
    reset,
  };
}
